<template>
  <stihl-section id="secPasskeys" :title="$t('dss.page.security.passkeys.title')" class="mt-5">

    <p class="form-text">
      <span>{{ $t('dss.page.security.passkeys.info') }}</span>&nbsp;
      <img class="action info" alt="info" src="/assets/icons/circle-info-colored.svg" @click="state = 'info'"/>
    </p>

    <div>{{ $t('dss.page.security.passkeys.registeredPasskeys') }}</div>

    <ul id="passkeys" class="list-group" v-if="hasPasskeys">
      <li class="list-group-item d-flex justify-content-between passkey"
          v-for="(passkey, uuid) in passkeys" :key="uuid" :id="uuid">
        <span>{{ passkey }}</span>
        <div class="icons">
          <img class="icon" alt="edit" src="/assets/icons/pencil.svg"
               @click="selected=uuid; passkeyName=passkey; state='newName'"/>
          <img class="icon" alt="delete" src="/assets/icons/trash.svg"
               @click="selected=uuid; state='confirmDelete'"/>
        </div>
      </li>
    </ul>
    <span data-testid="infoNoPasskeys" class="form-text" v-else>{{ $t('dss.page.security.passkeys.none') }}</span>

    <div class="buttons">
      <button id="addPasskey" type="button" class="btn btn-ghost" @click="addPasskey">
        <img class="btn-icon" src="/assets/icons/plus.svg" alt="add"/>
        <span>{{ $t('dss.page.security.passkeys.add.button') }}</span>
      </button>
    </div>

    <confirmation-modal id="confirmDelete" :title="$t('dss.page.security.passkeys.reallyDelete')"
                        :confirm="$t('dss.button.confirmDelete')"
                        @confirm="deletePasskey" @cancel="state=''"
                        v-if="state === 'confirmDelete'"/>

    <confirmation-modal id="editName"
                        :title="$t('dss.page.security.passkeys.edit.title')"
                        :confirm="$t('dss.button.save')"
                        @confirm="saveName" @cancel="state=''"
                        :disabled-confirm="v$.passkeyName.$invalid"
                        v-if="state === 'newName' || state === 'addName'">
      <form @submit.prevent="saveName">
        <group-input id="newName" class="mb-3"
                     :label="$t('dss.page.security.passkeys.edit.name')"
                     :required="true"
                     autocomplete="off"
                     v-model="v$.passkeyName.$model"
                     :validation-errors="v$.passkeyName.$errors"
                     @keyup.enter="saveName"/>
      </form>
    </confirmation-modal>

    <stihl-modal if="passkeyInfo" class="modal modal-lg" @modal-closed="state=''" v-if="state === 'info'">
      <div class="title-row">
        <h3 class="title" v-text="$t('dss.page.security.passkeys.about.title')"/>
      </div>
      <div class="modal-body-content">
        <p>{{ $t('dss.page.security.passkeys.about.info1') }}</p>
        <p>{{ $t('dss.page.security.passkeys.about.info2') }}</p>
        <p>{{ $t('dss.page.security.passkeys.about.info3') }}</p>
      </div>
    </stihl-modal>


  </stihl-section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import FormGroup from '@/components/ui/FormGroup.vue';
import StihlSection from '@/components/ui/StihlSection.vue';
import ConfirmationModal from '@/components/ui/ConfirmationModal.vue';
import StihlModal from '@/components/ui/StihlModal.vue';
import * as forgerock from '@forgerock/javascript-sdk';
import { CallbackType, FRStep, FRWebAuthn, StepType, WebAuthnStepType } from '@forgerock/javascript-sdk';
import OathRegistrationVerifier from './OathRegistrationVerifier.vue';
import StihlOverlay from '@/components/ui/StihlOverlay.vue';
import Backend from '@/components/backend';
import { i18n } from '@/lang';
import GroupInput from '@/components/ui/GroupInput.vue';
import useVuelidate from '@vuelidate/core';
import { maxLength, minLength, onlyValidCharacters } from '@/components/validators';

export default defineComponent({
  name: 'PasskeysSection',
  components: {
    GroupInput,
    StihlOverlay,
    OathRegistrationVerifier,
    StihlModal,
    ConfirmationModal,
    StihlSection,
    FormGroup
  },

  data() {
    return {
      passkeys: {} as Record<string, string>,
      // uuid of the selected passkey (on edit or delete)
      selected: '',
      // next step (on add)
      registerStep: undefined as FRStep | undefined,
      passkeyName: '',
      // state of the page (controls what is displayed)
      // add flow: '' -> 'add' -> 'addName' -> 'saving' -> ''
      // edit flow: '' -> 'newName' -> 'saving' -> ''
      // delete flow: '' -> 'confirmDelete' -> 'deleting' -> ''
      state: '' as '' | 'info' | 'add' | 'addName' | 'newName' | 'saving' | 'confirmDelete' | 'deleting',
    };
  },

  async mounted() {
    await this.loadPasskeys();
  },

  computed: {
    i18n: () => (i18n),
    hasPasskeys() {
      return this.passkeys && Object.getOwnPropertyNames(this.passkeys).length > 0;
    }
  },

  methods: {
    async loadPasskeys() {
      const backendAPI = await Backend.validateToken();
      this.passkeys = await backendAPI.loadPasskeys();
    },

    /**
     * Add a new passkey, by starting the 'passkey_registration' flow.
     * On success, the received FR step is saved and the "Naming" modal is opened.
     * NOTE: for testing locally set (Access-Control-Allow-Credentials: true)
     */
    async addPasskey() {
      this.state = 'add';

      const opts = {
        query: {
          'ForceAuth': 'true', // force authentication
          authIndexType: 'service',
          authIndexValue: import.meta.env.VITE_REGISTER_PASSKEY_TREE,
        },
      };

      try {
        // initiating the 'passkey_registration' flow
        const step = await forgerock.FRAuth.next(undefined, opts);
        console.log(`[passkey:add] new-step: ${step.type} [stage:${step.payload.stage}]`);

        // expect passkey registration step
        if (step.type === StepType.Step && step.getStage() === 'passkey'
            && FRWebAuthn.getWebAuthnStepType(step) === WebAuthnStepType.Registration) {
          // call passkey registration from the sdk
          const nextStep = await FRWebAuthn.register(step);
          console.debug('[passkey:add] register result:', step);

          // expect step
          if (nextStep.type === StepType.Step) {
            // save the step and open the "Naming" modal
            this.registerStep = step;
            this.passkeyName = 'my new passkey';
            this.state = 'addName';
            return;
          }
        }

        // something went wrong
        this.$toast.error(this.$t('dss.page.security.passkeys.add.error'));

      } catch (e: any) {
        console.info('[passkey:add] exception', e.code, e.name, e.message);
        if (e.name) { //  'NotAllowedError'
          this.$toast.warning(this.$t(e.message));
        } else {
          this.$toast.error(this.$t('dss.error.generic'));
        }
      }

      this.state = '';
    },

    /** called when a passkey is added or edited */
    async saveName() {

      if (this.state === 'saving') return; // already saving

      this.v$.passkeyName.$touch();
      if (this.v$.$invalid) return console.error('validation failed', this.v$.$errors);

      try {

        if (this.state === 'addName') {
          this.state = 'saving';
          await this.finishPasskeyRegistration();

        } else if (this.state === 'newName') {
          this.state = 'saving';
          await this.renamePasskey();

        } else {
          throw new Error('unexpected state: ' + this.state);
        }
      } finally {
        this.state = '';
      }
    },

    /** submit the step with the passkey name to finalize the registration */
    async finishPasskeyRegistration() {
      const registrationStep = this.registerStep as FRStep;
      if (!registrationStep) throw new Error('registerStep is undefined');

      // fill passkey name in callback
      registrationStep.getCallbackOfType(CallbackType.TextInputCallback).setInputValue(this.passkeyName);

      // submit step
      const step = await forgerock.FRAuth.next(registrationStep);
      console.log(`[passkey:add] new-step: ${step.type} [stage:${step.payload.stage}]`);
      if (step.type === StepType.LoginSuccess) {
        this.$toast.info(this.$t('dss.page.security.passkeys.add.success'));
        await this.loadPasskeys();
      } else {
        this.$toast.error(this.$t('dss.page.security.passkeys.add.error'));
      }
    },

    /** save the passkey name by calling the backend */
    async renamePasskey() {
      const backendAPI = await Backend.validateToken();
      try {
        const resp = await backendAPI.renamePasskey(this.selected, this.passkeyName);

        if (resp.status === 200) {
          this.passkeys[this.selected] = this.passkeyName;
          this.$toast.info(this.$t('dss.page.security.passkeys.edit.success'));
        } else {
          this.$toast.error(this.$t('dss.page.security.passkeys.edit.error'));
        }
      } catch (e) {
        this.$toast.error(this.$t('dss.error.generic'));
      }
    },

    async deletePasskey() {
      this.state = 'deleting';

      const backendAPI = await Backend.validateToken();
      try {
        const resp = await backendAPI.deletePasskey(this.selected);

        if (resp.status === 200) {
          delete this.passkeys[this.selected];
          this.$toast.info(this.$t('dss.page.security.passkeys.delete.success'));
        } else {
          this.$toast.error(this.$t('dss.page.security.passkeys.delete.error'));
        }
      } catch (e) {
        this.$toast.error(this.$t('dss.error.generic'));
      }

      this.selected = '';
      this.state = '';
    }
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      passkeyName: {
        minLength: minLength(3),
        maxLength: maxLength(127),
        onlyValidCharactersAllowed: onlyValidCharacters(/^[a-zA-Z0-9\s]+$/u),
      },
    };
  },
});
</script>

<style scoped>
.form-text .info {
  display: inline-block;
  vertical-align: -2px;
}
</style>
